<template>
  <el-form ref="newPatientForm" :model="newPatient" :rules="rules" label-position="top" :validate-on-rule-change="false">
    <PatientInfoSection
      :title="$t('OrderPage[\'Patient Info\']')"
      :sub-title="$t('OrderPage[\'Used to identify the patient\']')"
    >
      <Alert v-if="isShowDuplicatePatientWarning" :height="40" class="flex items-center mb-3">
        <span class="material-icons-outlined px-3">warning</span>
        This patient already exists in our data base
      </Alert>

      <div class="create-patient-form grid grid-cols-3 gap-x-9 gap-y-3.5 mb-3">
        <el-form-item 
          v-for="item in patientFormItems.slice(0, 4)" 
          :key="item.prop"
          :prop="item.prop"
        >
          <p :class="['label', {'font-medium': item.prop !== 'middleName'}]">{{ $t(item.label) }} <span v-if="item.prop !== 'middleName'" class="text-red-500">{{ $t('OrderPage["(Required)"]') }}</span></p>
          <el-input
            :id="item.id"
            :ref="item.ref"
            v-model="newPatient[item.prop]" 
            :placeholder="$t(item.placeholder)"
            :disabled="isInputDisabled(item.prop)"
            @blur="formItemBlur(item.prop)"
            @input="formItemInput(item.prop)"
          />
        </el-form-item>

      </div>

      <div class="create-patient-form grid grid-cols-3 gap-x-9 gap-y-3.5 mb-4">
        <!-- Phone section -->
        <div class="col-span-1">
          <p class="label font-medium">{{ $t('OrderPage[\'Phone Number\']') }} <span v-if="isPhoneRequired" style="color: #ef4444">{{ $t('OrderPage["(Required)"]') }}</span></p>
          <div class="grid grid-cols-7 gap-2">
            <div class="col-span-2">
              <el-select
                id="areacode-select"
                v-model="areaCodeInput"
                filterable
              >
                <template v-slot:prefix>
                  <img :src="flagIconURL" width="22">
                </template>
                <el-option
                  v-for="g in countryOptions"
                  :key="g.value"
                  :label="'+' + areaCodeMap[g.value]"
                  :value="'+' + areaCodeMap[g.value] + '/' + g.countryNameTwoBytes"
                >
                  <span style="float: left">{{ g.label }}</span>
                  <span style="float: right">{{ "+" + areaCodeMap[g.value] }}</span>
                </el-option>
              </el-select>
            </div>

            <el-form-item
              id="add-patient-phone-number-input"
              prop="phone"
              class="col-span-5"
            >
              <el-input
                ref="patientPhone"
                v-model="newPatient.phone"
                :placeholder="$t('OrderPage[\'Phone Number\']')"
                @blur="formItemBlur('phone')"
              />
            </el-form-item>
          </div>
        </div>

        <div class="col-span-2">
          <div class="grid grid-cols-2 gap-9">
            <el-form-item 
            v-for="item in patientFormItems.slice(4, 6)" 
            :key="item.prop"
            :prop="item.prop"
          >
          <p :class="['label', {'font-medium': item.prop !== 'middleName'}]">{{ $t(item.label) }} <span v-if="item.prop !== 'middleName'" style="color: #3498DB">{{ $t('OrderPage["(Required later)"]') }}</span></p>
            <el-select 
              v-if="['gender'].includes(item.prop)" 
              v-model="newPatient[item.prop]"
              :placeholder="$t(item.placeholder)"
              :filterable="item.prop === 'country' ? true : false"
              :filter-method="item.prop === 'country' ? filterCountryOptions : null"
              :disabled="isInputDisabled(item.prop)"
              @blur="formItemBlur(item.prop)"
            >
              <el-option
                v-for="g in item.options"
                :key="g.value"
                :label="$t(g.label)"
                :value="g.value"
              />
            </el-select>
            <el-input
              v-else
              :id="item.id"
              :ref="item.ref"
              v-model="newPatient[item.prop]" 
              :placeholder="$t(item.placeholder)"
              :disabled="isInputDisabled(item.prop)"
              @blur="formItemBlur(item.prop)"
              @input="formItemInput(item.prop)"
            />
            </el-form-item>
          </div>
          <Alert type="info" class="mt-1.5 py-2">
            <p class="require-later-alert">{{$t('OrderPage[\'To generate the final report, these 2 fields need to be completed either by the provider or your patient later.\']')}}</p>
          </Alert>
        </div>

        <div class="col-span-2">
          <div class="grid grid-cols-2 gap-x-4 gap-y-3.5 -mt-5 mb-5">
            <div class="col-span-2 mt-3">
              <div id="address-wrapper">
                <AddressAutofilledUnit
                  v-model="newPatient.streetAddress"
                  :default="newPatient.streetAddress"
                  :preferCountryCode="newPatient.country || null"
                  :question="$t('OrderPage[\'Street Address\']')"
                  @autofillAddress="updatePatientAddress"
                  @handleValueChange="formItemInput('streetAddress')"
                />
              </div>
            </div>
            <el-form-item 
              v-for="item in patientFormItems.slice(9, 13)" 
              :key="item.prop"
              :prop="item.prop"
              :label="$t(item.label)"
            >
              <div v-if="['country'].includes(item.prop)" class="w-full">
                <el-select 
                  v-model="newPatient[item.prop]"
                  :placeholder="$t(item.placeholder)"
                  filterable
                  :filter-method="filterCountryOptions"
                  @blur="formItemBlur(item.prop)"
                >
                  <el-option
                    v-for="g in item.options"
                    :key="g.value"
                    :label="g.label"
                    :value="g.value"
                  />
                </el-select>
                <!-- <p v-if="unsupportedCountruWarning" class="absolute unsupport-country-warning mt-1">
                  {{ unsupportedCountruWarning }}
                </p> -->
              </div>
              <el-input
                v-else
                :id="item.id"
                :ref="item.ref"
                v-model="newPatient[item.prop]" 
                :placeholder="$t(item.placeholder)"
                @blur="formItemBlur(item.prop)"
                @input="formItemInput(item.prop)"
              />
            </el-form-item> 
          </div>
          <Alert :height="35" type="warning" class="flex items-center mt-3">
            <img src="@/assets/orderTest/info_icon_yellow_24px.svg" class="pl-3 pr-2" alt="error outline">
            <p>{{$t('OrderPage[\'We DO NOT ship kits to P.O Boxes.\']')}}</p>
          </Alert>
          <CountryRestrictionAlert class="mt-3" />
        </div>

        <div class="grid grid-cols-2 gap-x-4">
          <div>
            <p class="label">{{$t('OrderPage[\'Patient Height\']')}}</p>
            <div class="grid grid-cols-2 gap-2">
              <el-form-item 
                :id="'create-patient-'+item.prop"
                v-for="item in patientFormItems.filter(item => ['heightInFeet','heightInInch'].includes(item.prop))" 
                :key="item.prop"
                :prop="item.prop"
              >
                <el-input 
                  v-model="newPatient[item.prop]" 
                  :placeholder="$t(item.placeholder)"
                  type="number"
                  min="0"
                  onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  @blur="formItemBlur(item.prop)"
                />
              </el-form-item>
            </div>
          </div>

          <el-form-item :label="$t('OrderPage[\'Patient Weight\']')">
            <el-input
              v-model="newPatient.weight"
              :placeholder="$t('OrderPage[\'Lbs\']')"
              type="number"
              min="0"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
              @blur="formItemBlur('weight')"
            />
          </el-form-item>
        </div>
      </div>
    </PatientInfoSection>

    <div class="flex mb-8">
      <LoginButton class="w-20 mr-4" @click="$emit('back')">
        {{ $t('OrderPage[\'Back\']') }}
      </LoginButton>
      <LoginButton 
        type="primary" 
        class="min-w-max w-32" 
        :loading="submitFormLoading"
        @click="submitForm('newPatientForm')"
      >
        {{ submitFormButtonText }}
      </LoginButton>
    </div>
  </el-form>
</template>

<script>
import PatientInfoSection from "./PatientInfoSection.vue";
import Alert from "../../../components/Alert.vue";
import CountryRestrictionAlert from "../../../country-restriction-alert/index.vue";
import AddressAutofilledUnit from "@/components/AddressForm/AddressAutofilledUnit.vue";
import LoginButton from "../../../../login/components/LoginButton.vue";
import { notify } from '@/utils/notify';
import { isValidEmail } from "@/utils/index.js";
import {
  createPatientFormRule,
  createPatientFormRuleAddress,
  createPatientFormRulePhone,
  createPatientFormItems,
  patient,
  fillInGoogleAddress,
  formatPatientForApi,
  checkBirthdateInput,
  changeFlagIcon,
  getUnsupportedCountryWarning
} from "./createPatientForm.js";
import { filterCountry } from "@/utils/va-order-test/filterCountry.js";
import { formatPhoneNumber } from "@/utils/va-order-test/checkPhoneInput.js";
import { addNewPatientApi, updatePatientApi } from "@/api/va-order-page/orderTest.js";
import { getStateAbbreviation } from "@/utils/va-order-test/parsePatient.js";
import { mapState } from "vuex";
import _ from "lodash";
const areaCodes = require("@/utils/va-order-test/phoneAreaCode/areaCodes.json");

export default {
  emits: ["back", "confirmUseDuplicatePatient", "createNewPatient", "editPatient", "duplicatePatient"],
  components:{
    PatientInfoSection,
    Alert,
    CountryRestrictionAlert,
    LoginButton,
    AddressAutofilledUnit
  },
  
  props: {
    patient: {
      type: Object,
      default: () => patient
    },
    mode: {
      type: String,
      default: "create"
    }
  },

  data() {
    return {
      originalPatient: {...this.patient},
      newPatient: this.patient,
      rules: createPatientFormRule(this.$t),
      createPatientFormRuleAddress,
      isShowingPhoneNotice: false,
      isShowingEmailNotice: false,      
      patientFormItems: [...createPatientFormItems],
      areaCodeMap: areaCodes,
      areaCodeInput: "+1",
      flagIconURL: "https://hatscripts.github.io/circle-flags/flags/us.svg",
      isShowDuplicatePatientWarning: false,
      submitFormLoading: false
    }
  },

  computed: {
    ...mapState("orderTest",["countryOptions"]),
    ...mapState("orderTest/user", ["userInfo"]),

    unsupportedCountruWarning() {
      return getUnsupportedCountryWarning(this.newPatient.country, this.countryOptions);
    },

    isPhoneRequired() {
      // Phone is required for non-US countries
      const usCodes = ['US'];
      return this.newPatient.country && !usCodes.includes(this.newPatient.country);
    },

    submitFormButtonText() {
      if (this.mode === "create") {
        return this.$t("OrderPage[\"Create Patient\"]");
      }
      if (this.mode === "edit") {
        return this.$t("OrderPage[\"Save\"]");
      }
      return this.$t("OrderPage[\"Submit Form\"]");
    }
  },

  watch: {
    "newPatient.birthdate"(newVal, oldVal) {
      checkBirthdateInput(newVal, oldVal, this.newPatient);
    },

    areaCodeInput(newVal) {
      let codeCountryTuple = newVal.split("/")
      if (codeCountryTuple && codeCountryTuple.length===2) {
        this.newPatient.areaCode = codeCountryTuple[0];
        this.flagIconURL = changeFlagIcon(codeCountryTuple[1].toLowerCase());
        // after area code change, format the phone number
        if (this.newPatient.phone) {
          this.newPatient.phone = formatPhoneNumber(this.newPatient.phone, this.newPatient.areaCode);
        }	
      }
    },
    '$i18n.locale'() {
      this.rules = createPatientFormRule(this.$t);
        this.$nextTick(() => {
        this.$refs.newPatientForm?.clearValidate();
      });
    },
    "newPatient.country": {
      handler(newVal) {
        this.updatePhoneRule(newVal);
      },
      immediate: true
    },
    // "newPatient.phone"(newVal, oldVal) {
    //   this.newPatient.phone = checkPhoneInput(newVal, oldVal);
    // },
  },

  async created() {
    await this.updateRules();
  },

  async mounted() {
    if (this.countryOptions.length === 0) {
      // get country options
      await this.$store.dispatch("orderTest/setCountryOptions");
    }
    let countryItemInForm = this.patientFormItems
      .find(item => item.prop === "country");
    countryItemInForm.options = this.countryOptions;
  },

  methods: {
    formItemBlur(prop) {
      switch(prop) {
        case "gender":
          this.$refs.newPatientForm.validateField("gender", (error) => {
            console.log(error);
          });
          break;
        case "email":
          this.validateEmail(this.newPatient[prop]);
          break;
        case "phone":
          this.newPatient[prop] = formatPhoneNumber(this.newPatient[prop], this.newPatient.areaCode);
          // this.validatePhone(this.newPatient[prop]);
          break;
        case "state":
          this.newPatient.state = getStateAbbreviation(this.newPatient.state).trim().toUpperCase();
          break;
        case "country":
          break;
        case "heightInFeet":
        case "heightInInch":
        case "weight":
          this.newPatient[prop] = this.newPatient[prop] 
            ? Math.round(Number(this.newPatient[prop])).toString()
            : "";
          break;
        default:
          this.newPatient[prop] = this.newPatient[prop].trim().toUpperCase();
          break;
      }
      
    },

    formItemInput(prop) {
      if (prop === "fullAddress") {
        fillInGoogleAddress(this.newPatient);
      }
    },

    submitForm(formName) {
      // check validation
      this.tryUpdateStreetFromDOM();
      this.$refs[formName].validate((valid) => {
        if (valid) { // if all fields are filled
          // check email and phone format
          // let phoneValid = this.validatePhone(this.newPatient.phone);
          // if (!phoneValid) {
          //   this.$refs.patientPhone.focus();
          //   return;
          // }
          let emailValid = this.validateEmail(this.newPatient.email)
          if (!emailValid) {
            this.$refs.patientEmail[0].focus();
            return
          }  
          if (!this.validateCountry()) {
            return;
          }
          let patientForApi = formatPatientForApi(this.newPatient);
          if (this.mode === "create") {
            this.createNewPatient(patientForApi);
          }
          if (this.mode === "edit") {
            this.editPatient(patientForApi, this.newPatient.id);
          }
        }
        else {
          let message = "Please fill all required fields.";
          notify({ type: 'error', message });
        }
      });
    },

    createNewPatient(patientForApi) {
      this.submitFormLoading = true;
      this.isShowDuplicatePatientWarning = false;
      addNewPatientApi(patientForApi).then(res => {
        notify({ type: 'success', title: 'Success!', message: 'successfully create patient.' });
        this.submitFormLoading = false;
        this.$emit("createNewPatient", { ...this.newPatient, id: res.data });
      }).catch(err => {
        this.submitFormLoading = false;
        if (!err.response) {
          return;
        }
        let { data, status } = err.response;
        // duplicate patient error
        if (status === 409) {
          this.newPatient.id = data.patientId;
          this.isShowDuplicatePatientWarning = true;
          this.$emit("duplicatePatient", data);
        }
      })
    },

    async editPatient(patientForApi, patientId) {
      this.submitFormLoading = true;
      let patientInfo = { ...patientForApi };
      try {
        ["first_name", "middle_name", "last_name"].forEach(prop => {
          delete patientInfo["patient_" + prop];
        });
        let payload = { patient_id: patientId, ...patientInfo };
        let resp = await updatePatientApi(payload);
        this.submitFormLoading = false;
        notify({ type: 'success', title: 'Success!', message: 'Successfully update patient information.' });
        this.$emit("editPatient", this.newPatient)
      } catch (err) {
        console.log("edit patient in create patient component error", err);
        this.submitFormLoading = false;
      }
      
    },

    validateCountry() {
      const fields = ["streetAddress", "city", "state", "zipcode", "country"];
      fields.forEach(field => {
        if (this.newPatient[field]) {
          this.newPatient[field] = this.newPatient[field].trim();
        }
      });
      
      const isAddressEmpty = fields.every(field => !this.newPatient[field]);
      if (isAddressEmpty) {
        return true;
      }  

      const filledCount = fields.filter(field => !!this.newPatient[field]).length;
      if (filledCount < 5) {
        notify({ type: 'error', title: 'Error', message: 'Please enter a complete address.' });
        return false;
      }
      
      const countryAliasMap = {
        "USA": "US",
        "UK": "GB",
        "UNITED KINGDOM": "GB",
        "GREAT BRITAIN": "GB"
      };
    
      const inputRaw = this.newPatient.country || "";
      let selected = inputRaw.toUpperCase();
      selected = countryAliasMap[selected] || selected;

      const match = this.countryOptions.find(
        c =>
          c.countryName?.toUpperCase() === selected ||
          c.countryNameTwoBytes?.toUpperCase() === selected
      );
    
      if (!match) {
        notify({ type: 'error', title: 'Error', message: `"${inputRaw}" is not a valid country.` });
        return false;
      }
      this.newPatient.country = match.countryNameTwoBytes;
      return true;
    },

    validateEmail(input) {
      if (!isValidEmail(input)) {
        if (!this.isShowingEmailNotice) {
          this.isShowingEmailNotice = true;
          notify({ type: 'error', title: this.$t("OrderPage[\"Error\"]"), message: this.$t("OrderPage[\"Please enter correct email format.\"]") });
          setTimeout(() => { this.isShowingEmailNotice = false }, 4000);
        }
        return false;
      }
      return true;
    },

    validatePhone(input) {
      if (input.length <= 5) {
        if (!this.isShowingPhoneNotice) {
          notify({ type: 'error', title: 'Error', message: 'Please enter correct phone format (xxx)-xxx xxxx.' });
          setTimeout(() => { this.isShowingPhoneNotice = false }, 4000);          
        }
        this.isShowingPhoneNotice = true;
        return false;
      }
      return true;  
    },

    filterCountryOptions(input) {
      let countryItemInForm = this.patientFormItems
        .find(item => item.prop === "country");
      countryItemInForm.options = filterCountry(input, this.countryOptions);
    },

    confirmUseDuplicatePatient(patientId) {
      this.isShowDuplicatePatientWarning = false;
      this.$emit('confirmUseDuplicatePatient', patientId)
    },

    updatePhoneRule(country) {
      // if patient address country is non-US, make phone required
      const usCodes = ['US'];

      if (country && !usCodes.includes(country)) {
        // Non-US country, add phone validation
        const phoneRule = createPatientFormRulePhone(this.$t);
        this.rules.phone = phoneRule.phone;
      } else {
        // US country or empty, remove phone validation
        delete this.rules.phone;
        this.$nextTick(() => {
          this.$refs.newPatientForm?.clearValidate('phone');
        });
      }
    },

    async updateRules() {
      // when edit patient mode in patient profile page, update the rule
      // if provider is non-us, make all address fields required
      
      const currentUrl = window.location.href;
      if (this.mode != "edit" || !currentUrl.includes("patient-profile")) {
        return;
      }
      if (_.isEmpty(this.userInfo)) {
        await this.$store.dispatch("orderTest/user/setUserInfo");
      }
      const us = ['United States', 'US', 'us', 'USA', 'UnitedStates', 'United States of America'];
      if (this.userInfo && this.userInfo.customer_country) {
        if (!us.includes(this.userInfo.customer_country) ) {
          Object.entries(this.createPatientFormRuleAddress).forEach(([key, value]) => {
              this.rules[key] = value;
          });
        }
        console.log(this.rules)
      }

    },
    isInputDisabled(prop) {
      let hasValue = ["birthdate", "gender"].includes(prop)
        ? this.mode === 'edit' && this.originalPatient[prop] 
        : this.mode === 'edit';
      return hasValue ? true : false;
    },

    tryUpdateStreetFromDOM() {
      try {
        const inputs = document.querySelectorAll('.address-autofill-input-div .el-input__inner');
        for (const input of inputs) {
          const val = input.value?.trim();
          if (val && this.newPatient.streetAddress === "") {
            this.newPatient.streetAddress = val;
            return;
          }
        }
      } catch (e) {
        console.warn("tryUpdateStreetFromDOM warm:", e);
      }
    },

    updatePatientAddress(address) {
      // console.log("address emit once", address);
      let street = `${address.street_number || ''} ${address.route || ''}`.trim();
      if (address.subpremise) {
        street = `${street} #${address.subpremise}`.trim();
      }
      this.newPatient.streetAddress = street;
      this.newPatient.city = address.locality || address.postal_town || address.administrative_area_level_2 || '';
      this.newPatient.state = address.administrative_area_level_1 || '';
      this.newPatient.zipcode = address.postal_code || '';
      this.newPatient.country = address.country || '';
    }
  }
}
</script>

<style scoped>
.label {
  height: 22px;
  color: #000000;
  font-family: Roboto;
  font-size: 14px;
  letter-spacing: 0.17px;
  line-height: 16px;
}
#create-patient-areacode {
  margin-top: 22px;
}
.unsupport-country-warning {
  color: #D5993D;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.divider {
  border-bottom: 1px solid #E6E6E6;
}
.require-later-alert {
  color:#4A4A4A;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin-left: 14px;
}
</style>
